import { GaugeSeriesOption } from "echarts";
import { SeriesTypeEnum } from "../enums/SeriesType.enum";
import { EchartsGraphConfig } from "../type/EchartGraphConfig";
import { GaugeSeries } from "../type/GaugeSeries";
import { StyleParam } from "../type/StyleParam";
import { StyleSize } from "../type/StyleSize";
import { Trigger } from "../type/Trigger";
import { EchartsGraph } from "./echarts-graph";

export type Graduation = 'axisTick' | 'splitLine';

export class EchartsGaugeGraph extends EchartsGraph {

    override trigger: Trigger = 'item';

    private graduationColor: string = '#63677a';
    private axisColor: string ='#e6ebf8';
    private unit: string = '';

    serie: GaugeSeriesOption = {
        type: SeriesTypeEnum.GAUGE,
        data: [],
        progress: {
            show: true
        },
        axisLine: {
            lineStyle: {
                width: 10,
                color: [[1, this.axisColor]]
            }
        },
        axisTick:  {
            lineStyle: {
                color: this.graduationColor
            }
        },
        splitLine: {
            lineStyle: {
                color: this.graduationColor
            }
        },
        axisLabel: {
            color: 'inherit',
        },
        detail: {
            fontSize: 20,
            offsetCenter: [0, 75],
            valueAnimation: true,
            formatter: (value: any) => {
                return value + ' ' + this.unit.toUpperCase();
            },
            color: 'inherit'
        }
    }

    constructor(config: EchartsGraphConfig){
        super(config);
        this.options.yAxis = [];
        if (config.unit.length > 0) this.unit = config.unit[0];
        if (config.tooltip === true) this.options.tooltip = this.setTooltip();
    }

    public override setSimpleGraphSeries(): void {
        throw new Error('Gauge serie should be set with setGaugeSerie()');
    }

    public setGaugeSerie(serie: GaugeSeries): void{
        this.serie.name = serie.name;
        this.serie.data = [serie.data];
        if (serie.min) this.serie.min = serie.min;
        if (serie.max) this.serie.max = serie.max;
        this.setSeries([this.serie])
    }

    public setAngles(start: number, end: number): void{
        this.serie.startAngle = start;
        this.serie.endAngle = end;
    }

    private changeGraduationStyle(target: Graduation, distance: number, length: number, width: number, color?: string){
        this.serie[target].distance = distance;
        this.serie[target].length = length;
        this.serie[target].lineStyle.width = width;
        if (color) this.changeGraduationColor(color, target);
    }

    public changeGraduationTicks(distance: number, length: number, width: number, color?: string): void{
        this.changeGraduationStyle('axisTick', distance, length, width, color)
    }

    public changeGraduationSplits(distance: number, length: number, width: number, color?: string): void{
        this.changeGraduationStyle('splitLine', distance, length, width, color)
    }

    public changeGraduationColor(color: string, target?: Graduation): void{
        if (!target || target === 'axisTick') this.serie.axisTick.lineStyle.color = color;
        if (!target || target === 'splitLine') this.serie.splitLine.lineStyle.color = color;
        if (!target) this.serie.axisLabel.color = color;
    }

    private setGraduationLabelParam(value: number | string, param: StyleParam): void {
        this.setParam(this.serie, null, null, value, param);
    }

    public setGraduationLabelSize(size: number): void {
        this.setGraduationLabelParam(size, 'fontSize');
    }

    public setGraduationLabelFont(font: string): void {
        this.setGraduationLabelParam(font, 'fontFamily');
    }

    public setGraduationLabelColor(color: string): void {
        if (!this.isValidColor(color)) return;
        this.setGraduationLabelParam(color, 'color');
    }

    public setGraduationLabel(size: number, font: string, distance: number | null = null, color: string | null = null){
        this.setGraduationLabelSize(size);
        this.setGraduationLabelFont(font);
        if (distance) this.setGraduationLabelDistance(distance);
        if (color) this.setGraduationLabelColor(color);
    }

    public setGraduationLabelDistance(distance: number){
        this.serie.axisLabel.distance = distance;
    }

    public setThickness(thickness: number): void{
        this.serie.axisLine.lineStyle.width = thickness;
    }

    public setValueFont(font: string){
        this.serie.detail.fontFamily = font;
    }

    public setValueSize(size: number){
        this.serie.detail.fontSize = size;
    }

    public setValueStyle(size: number, font: string){
        this.setValueFont(font);
        this.setValueSize(size);
    }

    public setValuePosition(x: StyleSize, y: StyleSize){
        const offsetX = x === 'center' ? 0 : this.setCardinality(x);
        const offsetY = y === 'center' ? 0 : this.setCardinality(y);
        this.serie.detail.offsetCenter = [offsetX, offsetY];
    }

    public changeAxisColor(colors: [number, string][]): void {
        colors.forEach(c => {
            if(!this.isValidColor(c[1])) return;
        });
        this.serie.axisLine.lineStyle.color = colors;
    }

    public showProgress(show: boolean): void {
        this.serie.progress.show = show;
    }

    public pointerInheritColor(){
        this.serie.pointer = {
            itemStyle: {
                color: 'inherit'
            }
        };
        this.serie.emphasis = {
            itemStyle: {
                color: 'inherit'
            }
        };
    }
}